import { Component, OnInit, OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ModalController, NavParams } from '@ionic/angular';

import { Ticket } from '@app/modules/model/ticket';

import { languageService, languageSupport } from '@app/modules/common/language';
import { viewService } from '@app/modules/view';

import { UiSummaryModal } from '@app/components/login/modal/summary/ui-summary-modal';

/************************************/
/* TICKET MODAL                     */
/************************************/

@Component({
    selector: 'modal-ticket',
    templateUrl:'ui-ticket-modal.html',
    styleUrls: [ 
        'ui-ticket-modal.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiTicketModal extends languageSupport implements OnInit, OnDestroy {
    public _ticket: Ticket = null;      // ticket object (to calculate information)
    public _tcknfo: any = null;         // precalculated ticket information

    get CanViewInvoice(){      // true if is an account ticket
        return (this._ticket.account != null) && (this._ticket.invoice == null) && (this._ticket.invceac != null);
    }
    
    get CanEditInvoice(){      // true if is not an account ticket
        return (this._ticket.IsPaid) && (!this._ticket.IsCancelled) && (this._ticket.account == null);    
    }

    get CanEditDueDate(){
        return !this._ticket.IsCancelled && !this._ticket.IsPaid;
    }

    get CanSendInvoice(){      // true if it is an invoice and we have the business email
        if (this._ticket.invoice == null){
            return false;
        }

        return (this._ticket.invoice.client.email != null)
    }

    get HasOptions(){
        if (this.OnInvoice || this.OnDueDate){
            return false;
        }

        return this.CanViewInvoice || this.CanEditInvoice || this.CanEditDueDate;
    }

    private _showoptions = false;
    get ShowOptions(){
        return this._showoptions;
    }

    set ShowOptions(value){
        this._showoptions = value;
        this.change.markForCheck();
    }

    /****************************/
    /* STARTS HERE              */
    /****************************/

    private _ticket_subscription = null;

    constructor(private lang: languageService, private change: ChangeDetectorRef, private modalCtrl: ModalController, private params: NavParams, public view: viewService) {
        super(lang, change);
    }
        
    ngOnInit(){
        this._ticket = this.params.get('ticket');
        this._tcknfo = this.params.get('tcknfo');

        if (this._ticket){
            this._ticket_subscription = this._ticket.OnRefresh.subscribe(
            data => {
                this.change.markForCheck();
            });
        }
    }
    
    ngOnDestroy(){
        super.OnDestroy();

        if (this._ticket_subscription){
            this._ticket_subscription.unsubscribe();
            this._ticket_subscription = null;
        }
    }

    OnClose(){
        this.modalCtrl.dismiss(null);
    }

    /****************************/
    /* EVENT HANDLERS           */
    /****************************/

    public _printing = false;
    PrintTicket(){
        this._printing = true;
        this._ticket.DoPrint(false);
        this.OnClose();
    }

    async SendInvoice(){
        this._ticket.DoEmail();

        setTimeout(() => {
            this.ShowOptions = false;
        }, 100);        
    }

    get IsPaid(){
        return this._ticket.IsPaid;
    }

    OnOptions(){
        this.ShowOptions = !this.ShowOptions;
    }

    private _duedays = null;
    get DueDays(): number {
        return parseInt(this._duedays) || this._ticket.duedays;
    }

    set DueDays(value: number){
        this._duedays = value;
        this.change.markForCheck();
    }

    get TicketDueDate(){
        let _date = null;
        if (this._ticket.invoice){
            _date = new Date(this._ticket.invoice.created);
        }
        else {
            _date = new Date(this._ticket.created);
        }

        _date.setDate(_date.getDate() + this.DueDays);

        const day = _date.getDate().toString().padStart(2, '0');
        const month = (_date.getMonth() + 1).toString().padStart(2, '0');
        const year = _date.getFullYear();
    
        return `${day}/${month}/${year}`;        
    }

    private _onduedate = false;
    get OnDueDate(){
        return this._onduedate;
    }

    set OnDueDate(value){
        this._onduedate = value;
        this.change.markForCheck();
    }

    async OnDueDaysAccept(){
        this._ticket.duedays = parseInt(this._duedays);
        await this._ticket.DoCommit();

        this._duedays = null;
        this.OnDueDate = false;        
    }

    OnDueDaysCancel(){
        this._duedays = null;
        this.OnDueDate = false;
    }

    EditDueDate(){
        this.OnDueDate = true;

        setTimeout(() => {
            this.ShowOptions = false;
        }, 100);        
    }

    private _oninvoice = false;
    get OnInvoice(){
        return this._oninvoice;
    }

    set OnInvoice(value){
        this._oninvoice = value;
        this.change.markForCheck();
    }

    EditInvoice(){
        this.OnInvoice = true;

        setTimeout(() => {
            this.ShowOptions = false;
        }, 100);        
    }

    OnInvoiceDone(){
        this.OnInvoice = false;
    }

    private _waitmodal = false;
    async ViewInvoice(){
        if (!this._waitmodal){
            this._waitmodal = true;
            const modal = await this.modalCtrl.create({
                component: UiSummaryModal,
                cssClass: 'ticket-modal',
                componentProps: { 
                    payment: this._ticket.invceac
                }                                    
            });

            await modal.present();
            
            setTimeout(() => {
                this._waitmodal = false;
            }, 100);
        }
        
        setTimeout(() => {
            this.ShowOptions = false;
        }, 100);         
    }
}
